import { useEffect, useMemo, useRef, useState } from 'react';
import { translateConversation } from '../../api/communicationApi';
import { ACCOUNT_LANGUAGES } from '../../utils/accountLanguage';

export const translationSignature = (message) => JSON.stringify([
  message.subject || '', message.content || '', message.content_html || '', message.attachments || [],
]);

// Cache only within this component/session. Account, conversation and target
// language changes cannot reuse or display another scope's translation.
export default function useLiveTranslation(conversationId, messages, language, userId) {
  const scope = `${userId || ''}:${conversationId || ''}:${language}`;
  const latest = useRef(null);
  latest.current = { scope, messages };
  const cache = useRef({ scope: '', entries: new Map() });
  const active = useRef(null);
  const [state, setState] = useState({ scope: '', entries: new Map(), busy: false, error: null });
  const [retryVersion, setRetryVersion] = useState(0);
  const [originalScope, setOriginalScope] = useState(null);
  const signature = useMemo(() => JSON.stringify(messages.map((msg) => [msg.id, translationSignature(msg)])), [messages]);

  useEffect(() => {
    if (!conversationId || !userId) return;
    if (active.current?.scope === scope && !active.current.controller.signal.aborted) return;
    active.current?.controller.abort();
    if (cache.current.scope !== scope) cache.current = { scope, entries: new Map() };
    const job = { scope, controller: new AbortController() };
    active.current = job;
    const publish = (busy, error = null) => {
      if (active.current === job && latest.current.scope === scope) {
        setState({ scope, entries: new Map(cache.current.entries), busy, error });
      }
    };
    const run = async () => {
      publish(true);
      try {
        while (!job.controller.signal.aborted && latest.current.scope === scope) {
          const visible = latest.current.messages;
          const ids = new Set(visible.map((msg) => msg.id));
          for (const id of cache.current.entries.keys()) {
            if (!ids.has(id)) cache.current.entries.delete(id);
          }
          const pending = visible.filter((msg) => (
            (msg.subject || msg.content || msg.content_html)
            && cache.current.entries.get(msg.id)?.signature !== translationSignature(msg)
          )).slice(0, 20);
          if (!pending.length) break;
          const requested = new Map(pending.map((msg) => [msg.id, translationSignature(msg)]));
          const result = await translateConversation(conversationId, {
            target_language: language,
            message_ids: pending.map((msg) => msg.id),
          }, { signal: job.controller.signal });
          if (latest.current.scope !== scope || active.current !== job) return;
          const returned = new Map((result.messages || []).map((item) => [item.message_id, item]));
          for (const [id, sourceSignature] of requested) {
            const item = returned.get(id);
            if (!item?.translated_text) throw new Error('Die Liveübersetzung ist unvollständig.');
            cache.current.entries.set(id, { ...item, signature: sourceSignature });
          }
          publish(true);
        }
        publish(false);
      } catch (error) {
        if (!job.controller.signal.aborted) publish(false, error);
      } finally {
        if (active.current === job) active.current = null;
      }
    };
    run();
    // New messages join the running queue instead of cancelling its request.
  }, [conversationId, userId, scope, signature, language, retryVersion]);

  useEffect(() => () => active.current?.controller.abort(), []);

  const option = ACCOUNT_LANGUAGES.find((item) => item.key === language) || ACCOUNT_LANGUAGES[0];
  const entries = state.scope === scope ? state.entries : new Map();
  const translatedMessages = messages.flatMap((msg) => {
    const entry = entries.get(msg.id);
    return entry?.signature === translationSignature(msg) ? [entry] : [];
  });
  const showOriginal = originalScope === scope;
  return {
    inlineTranslation: showOriginal ? null : {
      languageKey: language, languageLabel: option.label, targetLanguageName: option.nativeName,
      messages: translatedMessages,
    },
    hasTranslations: translatedMessages.length > 0,
    translating: state.scope === scope && state.busy,
    translationError: state.scope === scope ? state.error : null,
    showOriginal,
    toggleOriginal: () => setOriginalScope((current) => current === scope ? null : scope),
    retryTranslation: () => setRetryVersion((version) => version + 1),
  };
}
